import { Box, Flex, Icon, Text, VStack, chakra } from "@chakra-ui/react";
import { ArrowRight } from "@phosphor-icons/react";
import { asHTML, asText } from "@prismicio/client";
import { resolvePrismicLink } from "utils/prismicLinks";
import { richTextSx } from "./richTextStyles";

// Colors match the website's Link slice (antaia-website src/slices/Link)
const GREEN = "#5A8A1A";
// Same dark ink used for every heading in this component (section heading, each
// link's heading_link/heading_and_link).
const HEADING_INK = "#0E3137";

// Splits a sub-heading into its first sentence and the remainder, so the first
// reads in the same ink as every other heading and the rest reads in the brand
// green — e.g. "New to Antaia? Start Here" -> "New to Antaia?" / " Start Here".
// Splits on the first sentence-ending punctuation (. ! ?); if there's no
// sentence break, the whole string is treated as the (only) first sentence.
function splitFirstSentence(text) {
  const match = text.match(/^(.*?[.!?])(\s*)(.*)$/s);
  if (!match) return { first: text, rest: "" };
  return { first: match[1], rest: match[2] + match[3] };
}

// Second sub-heading's lead-in as a lighter lozenge (e.g. "Returning User?"),
// with the remainder of the sentence following as plain text.
function SubHeadingLozenge({ text, mt }) {
  const { first, rest } = splitFirstSentence(text);
  return (
    <Flex align="center" gap={2} mt={mt} flexWrap="wrap">
      <Lozenge text={first} light />
      {rest && (
        <Text fontSize="body.m" fontWeight="600" color={GREEN}>
          {rest}
        </Text>
      )}
    </Flex>
  );
}

// Same lozenge shape as the hero's tag (HomeHeroSlice.jsx), inverted for a light
// background: dark green fill with light text, rather than the hero's light-tint
// fill on a dark background. `light` swaps to a quieter lightLime-on-green-text
// treatment — used for the secondary "Returning User?" lead-in, so it doesn't
// compete with the promoted card's own "New to Antaia?" lozenge above it.
function Lozenge({ text, light }) {
  return (
    <Text
      display="inline-block"
      px={3}
      py={1.5}
      fontSize="12px"
      fontWeight="600"
      bg={light ? "lightLime" : GREEN}
      color={light ? GREEN : "white"}
      borderRadius="full"
    >
      {text}
    </Text>
  );
}

// Same "Antaia arrow" watermark used across the platform's promoted/CTA cards
// (e.g. AIDashboard.jsx's "New Review" card) — three lime chevron strokes bleeding
// off the bottom-right corner at low opacity.
function AntaiaArrowWatermark() {
  return (
    <chakra.svg
      position="absolute"
      right="0"
      bottom="0"
      width="120px"
      height="98px"
      viewBox="0 0 175 114"
      preserveAspectRatio="xMaxYMax meet"
      fill="none"
      pointerEvents="none"
    >
      <path d="M31.2441 113.272C31.2066 113.31 31.1694 113.349 31.1318 113.387H0C4.3128 108.084 9.24419 102.6 14.8643 96.9404L86.3467 25.458L64.8271 25.4736L64.7959 2.35255H142.156L31.2441 113.272Z" fill="#85C22A" fillOpacity="0.15" />
      <path d="M174.876 18.7002L80.2031 113.387H47.501L158.521 2.36036H174.876V18.7002Z" fill="#85C22A" fillOpacity="0.15" />
      <path d="M174.884 112.425H174.999V113.387H174.87C174.873 113.069 174.876 112.751 174.876 112.433L151.763 112.362V90.6221L128.998 113.387H96.2949L174.884 34.7969V112.425Z" fill="#85C22A" fillOpacity="0.15" />
    </chakra.svg>
  );
}

// Promoted card's "New to Antaia?" affordance — one filled pill button with the
// label and the arrow together (was previously two separate pieces: a plain
// lozenge up top and an unlabelled circular arrow button down at bottom-left).
function PromotedCta({ text }) {
  return (
    <Flex display="inline-flex" align="center" gap={2} pl={4} pr={3} py={2} borderRadius="full" bg={GREEN}>
      <Text fontSize="12px" fontWeight="600" color="white">
        {text}
      </Text>
      <Flex align="center" justify="center" boxSize="20px" borderRadius="full" bg="whiteAlpha.300" flexShrink={0}>
        <ArrowRight color="white" size={12} weight="bold" />
      </Flex>
    </Flex>
  );
}

// One row — promoted is a filled lightLime card with a lime keyline and the
// platform's Antaia arrow watermark (matches CTA cards elsewhere, e.g.
// AIDashboard.jsx's "New Review" card), secondary is a plain list row with a
// bottom divider (no card/keyline). `descriptionText` is already a plain string
// (both rich-text-array and plain-string description fields are normalized to a
// string by the caller, since the two Prismic field groups below use different
// field types for the same purpose). Promoted rows show a single "New to Antaia?"
// pill button (text + arrow together, PromotedCta) above the heading text — this
// used to be two separate pieces, a plain lozenge here and an unlabelled circular
// arrow button pinned to the bottom-left, now merged into one. On the Help home
// page (the Entry article's 3-column row) the promoted button's text is always
// "New to Antaia?" regardless of what's authored in the `lozenge` field —
// everywhere else, the authored `lozenge` text is used (falling back to nothing
// if it isn't set).
function LinkRow({ resolved, onNavigate, headingText, descriptionText, isPromoted, lozengeText, isHomePage }) {
  const promotedLozengeText = isHomePage ? "New to Antaia?" : lozengeText;
  return (
    <Flex
      as={resolved?.kind === "external" ? "a" : "div"}
      href={resolved?.kind === "external" ? resolved.url : undefined}
      target={resolved?.kind === "external" ? "_blank" : undefined}
      rel={resolved?.kind === "external" ? "noopener noreferrer" : undefined}
      onClick={resolved?.kind === "internal" ? () => onNavigate(resolved.uid) : undefined}
      justify="space-between"
      align={isPromoted ? "flex-start" : "center"}
      bg={isPromoted ? "lightLime" : "transparent"}
      border={isPromoted ? "1px solid" : "none"}
      borderBottom={isPromoted ? undefined : "1px solid"}
      borderColor={isPromoted ? "lime" : "gray.100"}
      borderRadius={isPromoted ? "lg" : 0}
      p={isPromoted ? 5 : 4}
      mb={isPromoted ? 3 : 0}
      // Fixed height so the 3 promoted cards line up across the Entry article's
      // 3-column row even though each section's heading/description text wraps to
      // a different number of lines.
      minH={isPromoted ? "180px" : undefined}
      cursor={resolved ? "pointer" : undefined}
      _hover={resolved ? { bg: isPromoted ? "lightLime" : "gray.50" } : undefined}
      _last={isPromoted ? undefined : { borderBottom: "none" }}
      transition="background-color 0.15s"
      position={isPromoted ? "relative" : undefined}
      overflow={isPromoted ? "hidden" : undefined}
    >
      {isPromoted && <AntaiaArrowWatermark />}
      <Box position="relative" zIndex={1}>
        {isPromoted && promotedLozengeText && (
          <Box mb={3}>
            <PromotedCta text={promotedLozengeText} />
          </Box>
        )}
        {headingText && (
          <Text fontWeight="700" fontSize={isPromoted ? "body.xl" : "body.l"} color={HEADING_INK}>
            {headingText}
          </Text>
        )}
        {descriptionText && (
          <Text fontSize="body.m" color="gray.500" mt={1}>
            {descriptionText}
          </Text>
        )}
      </Box>
      {!isPromoted && <ArrowRight color={resolved ? GREEN : "#CBD5E0"} />}
    </Flex>
  );
}

export default function LinkSlice({ slice, onNavigate, headingIcon, isHomePage }) {
  const { heading, content, sub_heading2, link, links } = slice.primary;
  // "helpLinks" variation authors promoted and secondary links as two separate
  // Prismic groups (`link` = promoted, `links` = secondary) rather than inferring
  // promotion from list position — see LinkSlice's original (default variation)
  // single-list behavior below, kept for older/simpler link slices (e.g. FAQs).
  const isHelpLinksVariation = slice.variation === "helpLinks";

  return (
    <VStack align="stretch" gap={4}>
      {heading && (
        <Flex align="center" gap={2}>
          {headingIcon && <Icon as={headingIcon} boxSize={5} color={HEADING_INK} />}
          <Text fontSize="24px" fontWeight="700" color={HEADING_INK}>
            {heading}
          </Text>
        </Flex>
      )}
      {content?.length > 0 && (
        // Fixed min-height so the promoted card below starts at the same vertical
        // position across all 3 Entry-article columns, regardless of how many lines
        // this section's intro text happens to wrap to (sized for a 3-line
        // paragraph @ body.m/1.7 line-height ≈ 82px). The paragraph's own bottom
        // margin is zeroed here — without that, its margin adds on top of minH so a
        // 3-line paragraph ends up taller than a 2-line one even with the same
        // minH (margin doesn't count toward it), which is what caused the
        // misalignment. Spacing below the paragraph comes from the VStack's own
        // gap instead.
        <Box
          minH={isHelpLinksVariation ? "82px" : undefined}
          sx={isHelpLinksVariation ? { ...richTextSx, p: { ...richTextSx.p, mb: 0 } } : richTextSx}
          dangerouslySetInnerHTML={{ __html: asHTML(content) }}
        />
      )}

      {isHelpLinksVariation ? (
        <>
          {link?.length > 0 && (
            <VStack align="stretch" gap={0}>
              {link.map((item, index) => (
                <LinkRow
                  key={index}
                  resolved={resolvePrismicLink(item.page_to_link_to)}
                  onNavigate={onNavigate}
                  headingText={item.heading_link}
                  descriptionText={item.description_of_what_you_will_see?.length > 0 ? asText(item.description_of_what_you_will_see) : null}
                  isPromoted
                  lozengeText={item.lozenge}
                  isHomePage={isHomePage}
                />
              ))}
            </VStack>
          )}
          {sub_heading2 && <SubHeadingLozenge text={sub_heading2} mt={2} />}
          {links?.length > 0 && (
            <VStack align="stretch" gap={0}>
              {links.map((item, index) => (
                <LinkRow
                  key={index}
                  resolved={resolvePrismicLink(item.article_link)}
                  onNavigate={onNavigate}
                  headingText={item.heading_and_link}
                  descriptionText={item.description_of_what_you_will_see || null}
                  isPromoted={Boolean(item.is_promotional_link)}
                />
              ))}
            </VStack>
          )}
        </>
      ) : (
        link?.length > 0 && (
          <VStack align="stretch" gap={0}>
            {link.map((item, index) => (
              // The first link in the list is the promoted CTA for this section — the
              // rest read as a plain list, so the promoted link stands out.
              <LinkRow
                key={index}
                resolved={resolvePrismicLink(item.page_to_link_to)}
                onNavigate={onNavigate}
                headingText={item.heading_link}
                descriptionText={item.description_of_what_you_will_see?.length > 0 ? asText(item.description_of_what_you_will_see) : null}
                isPromoted={index === 0}
              />
            ))}
          </VStack>
        )
      )}
    </VStack>
  );
}
